import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useNetwork } from '@/contexts/NetworkContext'
import type { EarningsAccount } from '@/services/earnings/earningsAccountsApi'
import type { Targeting } from '@/services/targeting/targeting'
import {
  SEGMENT_TYPES,
  SegmentsApi,
  type SegmentRef,
  type SegmentType,
} from '@/services/targeting/targetingLookupApi'
import { enumLabel } from '@/utils/enumLabel'
import { formatCurrencyAmount } from '@/utils/money'
import { EarningsAccountSelect } from '../EarningsAccountSelect'
import { SearchInput } from '../SearchInput'
import { countInventory, readInventory } from './InventoryTargetEditor'

export interface SegmentSearchProps {
  /** The buttons in a segment's row — what picking it does is up to the editor. */
  actions: (segment: SegmentRef) => React.ReactNode
  /** Shown above the table, e.g. which group a segment will be added to. */
  hint?: React.ReactNode
  /** The targeting being edited; its inventory narrows the publishers on a marketplace. */
  targeting?: Targeting
  /**
   * Every segment the targeting names, included and NOT, with what the lookup
   * knows of each — what "Show selected" lists.
   */
  selected: SegmentRef[]
}

const PAGE_SIZE = 50
const ALL = 'all'

/**
 * The network's segments as a table to pick from, searched and filtered on
 * the server and paged the way the API pages them. On a marketplace the
 * segments come from the publishers, so they are kept to the publishers the
 * inventory targeting reaches, and can be narrowed to one of those.
 *
 * "Show selected" turns the table to the segments already picked, so their
 * type, publishers and price can be seen without finding them again.
 */
export function SegmentSearch({ actions, hint, targeting, selected }: SegmentSearchProps) {
  const { t } = useTranslation()
  const { currentNetwork } = useNetwork()
  const marketplace = !!currentNetwork?.marketplacePlatform
  const [term, setTerm] = useState('')
  const [type, setType] = useState<SegmentType | typeof ALL>(ALL)
  const [publisherId, setPublisherId] = useState('')
  const [page, setPage] = useState(1)
  const [onlySelectedPicked, setOnlySelected] = useState(false)
  // Turns itself off once nothing is picked, rather than leaving an empty table.
  const onlySelected = onlySelectedPicked && selected.length > 0

  /**
   * The picked segments, in the targeting's order, narrowed by the filters the
   * way the server narrows the full list.
   */
  const selectedRows = useMemo(() => {
    if (!onlySelected) return []
    const needle = term.trim().toLowerCase()
    return selected
      .filter(
        (segment) =>
          !needle ||
          [segment.name, segment.id, segment.description].some((text) =>
            text?.toLowerCase().includes(needle)
          )
      )
      .filter((segment) => type === ALL || segment.type === type)
      .filter(
        (segment) =>
          !publisherId || !!segment.publishers?.some((publisher) => publisher.id === publisherId)
      )
  }, [onlySelected, selected, term, type, publisherId])

  const inventory = useMemo(() => targeting && readInventory(targeting), [targeting])
  // The server works out the publishers from the targeting, and ignores a
  // picked publisher while it does, so a picked one is asked for on its own.
  // Off a marketplace segments carry no publishers, and narrowing by them
  // would hide every one.
  const scoped = marketplace && !publisherId && !!inventory && countInventory(inventory) > 0
  // Only the inventory decides what comes back, so picking a segment — which
  // changes the targeting too — does not fetch the page again.
  const scopeKey =
    scoped && inventory
      ? JSON.stringify([
          inventory.countries,
          inventory.publishers.map((ref) => ref.id),
          inventory.sites.map((ref) => ref.id),
          inventory.adUnits.map((ref) => ref.id),
          inventory.negated,
        ])
      : ''

  /**
   * The publishers the inventory leaves in play, from what the publisher list
   * already holds: the targeted publishers, less the excluded ones, with a
   * site in a targeted country.
   */
  const inScope = useCallback(
    (account: EarningsAccount) => {
      if (!inventory) return true
      const ids = inventory.publishers.map((ref) => ref.id)
      if (ids.length && ids.includes(account.id) === inventory.negated.publisher) return false
      return (
        !inventory.countries.length ||
        account.countries.some((country) => inventory.countries.includes(country))
      )
    },
    [inventory]
  )

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['targeting-segment-search', term, type, publisherId, scopeKey, page],
    queryFn: () =>
      SegmentsApi.search({
        q: term,
        type: type === ALL ? undefined : type,
        publisherId: marketplace ? publisherId : undefined,
        targeting: scoped ? targeting : undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: !onlySelected,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  })

  const results = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  // A new filter starts over from the first page.
  const filter =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value)
      setPage(1)
    }

  const columns = useMemo<ColumnDef<SegmentRef, unknown>[]>(
    () => [
      {
        id: 'name',
        header: t('common.name'),
        enableSorting: false,
        meta: { minWidth: 220 },
        // An uploaded segment often has no name, leaving its id to stand in.
        // Cells do not wrap by default, and a long description would push
        // the buttons out of sight, so this one wraps and stops at two lines.
        cell: ({ row: { original: segment } }) => {
          const meta = [segment.name && segment.id, segment.description].filter(Boolean).join(' · ')
          return (
            <div className="flex min-w-0 flex-col whitespace-normal">
              <span className="flex flex-wrap items-center gap-1.5 font-medium break-words">
                {segment.name || segment.id}
                {segment.objectState && segment.objectState !== 'ACTIVE' && (
                  <Badge variant="gray">{enumLabel(segment.objectState)}</Badge>
                )}
              </span>
              <span className="line-clamp-2 text-xs break-words text-muted-foreground" title={meta}>
                {meta}
              </span>
            </div>
          )
        },
      },
      {
        id: 'dataSource',
        header: t('targeting.segment.dataSource'),
        enableSorting: false,
        cell: ({ row: { original: segment } }) =>
          segment.dataSource ? <Badge variant="gray">{enumLabel(segment.dataSource)}</Badge> : '—',
      },
      {
        id: 'type',
        header: t('common.type'),
        enableSorting: false,
        cell: ({ row: { original: segment } }) =>
          segment.type ? (
            <Badge variant="blue">{t(`targeting.segment.types.${segment.type}`)}</Badge>
          ) : (
            '—'
          ),
      },
      ...(marketplace
        ? [
            {
              id: 'publishers',
              header: t('targeting.segment.publishers'),
              enableSorting: false,
              cell: ({ row: { original: segment } }) => (
                <span className="whitespace-normal">
                  {segment.publishers
                    ?.map((publisher) => publisher.name || publisher.id)
                    .join(', ') || '—'}
                </span>
              ),
            } satisfies ColumnDef<SegmentRef, unknown>,
          ]
        : []),
      {
        id: 'cpm',
        header: t('targeting.segment.cpm'),
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row: { original: segment } }) => formatCurrencyAmount(segment.cpm),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">{t('common.actions')}</span>,
        enableSorting: false,
        enableResizing: false,
        meta: { align: 'right' },
        cell: ({ row: { original: segment } }) => (
          <div className="flex justify-end gap-1.5">{actions(segment)}</div>
        ),
      },
    ],
    [t, marketplace, actions]
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={term}
          onChange={filter(setTerm)}
          placeholder={t('targeting.searchType', {
            what: t('targeting.types.userSegment.plural').toLowerCase(),
          })}
          className="w-72"
        />
        <Select value={type} onValueChange={filter((v: string) => setType(v as SegmentType))}>
          <SelectTrigger className="h-8 w-48 text-sm" aria-label={t('common.type')}>
            <SelectValue>
              {type === ALL
                ? t('targeting.segment.allTypes')
                : t(`targeting.segment.types.${type}`)}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('targeting.segment.allTypes')}</SelectItem>
            {SEGMENT_TYPES.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`targeting.segment.types.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {marketplace && (
          <EarningsAccountSelect
            value={publisherId}
            onChange={filter(setPublisherId)}
            anyLabel={t('targeting.segment.allPublishers')}
            include={inScope}
            className="h-8 w-56"
          />
        )}
        {isFetching && !isLoading && (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        )}
        {selected.length > 0 && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="ml-auto h-auto p-0 text-xs"
            aria-pressed={onlySelected}
            onClick={() => setOnlySelected(!onlySelected)}
          >
            {onlySelected
              ? t('targeting.column.showAll')
              : `${t('targeting.column.showSelected')} (${selected.length})`}
          </Button>
        )}
      </div>

      {scoped && !onlySelected && (
        <p className="text-xs text-muted-foreground">{t('targeting.segment.limitedToInventory')}</p>
      )}
      {hint}

      <div className="rounded-md border">
        <DataTable
          columns={columns}
          data={onlySelected ? selectedRows : results}
          loading={!onlySelected && isLoading}
          emptyMessage={t('targeting.noResults')}
          maxHeight={420}
        />
      </div>

      {!onlySelected && totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
          <span>
            {t('targeting.segment.showing', {
              from: (page - 1) * PAGE_SIZE + 1,
              to: Math.min(page * PAGE_SIZE, totalCount),
              total: totalCount,
            })}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('common.previous')}
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('common.next')}
            disabled={page * PAGE_SIZE >= totalCount}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      )}
    </div>
  )
}
